import type { OutputLayer } from '@/components/preview/output-layer-preview';
import { collectOutputLayerTrackSids } from '@/helpers/resolve-output-preview-layers';
import { normalizeMediaItemId } from '@/helpers/resolve-live-sequence-item-ids.helper';

const CONTENT_COMPONENT_IDS = new Set([
  'html',
  'video',
  'backgroundVideo',
  'image',
  'imageSequence',
  'presentationSlides',
  'pdf',
  'audio',
]);

function isVisibleAgentLiveKitLayer(layer: OutputLayer): boolean {
  if (layer.visible === false) {
    return false;
  }
  return (
    layer.componentId === 'livekitTrackMediaAgent' ||
    (typeof layer.itemId === 'string' && layer.itemId.startsWith('mediaAgent:'))
  );
}

function agentLiveKitIsAttachable(layer: OutputLayer): boolean {
  if (collectOutputLayerTrackSids(layer).length > 0) {
    return true;
  }
  return typeof layer.itemId === 'string' && layer.itemId.startsWith('mediaAgent:');
}

function layerTargetsMediaItem(layer: OutputLayer, currentItemId: string): boolean {
  if (layer.itemId === currentItemId) {
    return true;
  }
  const bare = currentItemId.startsWith('media:') ? currentItemId.slice('media:'.length) : currentItemId;
  return layer.data?.mediaId === bare;
}

/**
 * True when Current already has the same on-air commit the players are showing.
 * Not "layers exist" / compute done — the outgoing agent track stays attachable across a
 * deck switch, so agent takes also require {@link activeAgentDeckMediaId} to match Current.
 */
export function isOperatorCurrentPaintReady(params: {
  currentItemId?: string;
  isAgentDeck: boolean;
  layers: OutputLayer[];
  activeAgentDeckMediaId?: string;
}): boolean {
  const currentItemId = params.currentItemId;
  if (!currentItemId || params.layers.length === 0) {
    return false;
  }

  const stillsReady = params.layers.some(
    (layer) =>
      layer.visible !== false &&
      CONTENT_COMPONENT_IDS.has(layer.componentId) &&
      layerTargetsMediaItem(layer, currentItemId),
  );

  if (!params.isAgentDeck) {
    return stillsReady;
  }

  if (stillsReady) {
    return true;
  }

  const currentMediaId = normalizeMediaItemId(currentItemId);
  const onAirDeckId = normalizeMediaItemId(params.activeAgentDeckMediaId);
  if (!currentMediaId || !onAirDeckId || currentMediaId !== onAirDeckId) {
    return false;
  }

  return params.layers.some((layer) => isVisibleAgentLiveKitLayer(layer) && agentLiveKitIsAttachable(layer));
}
